/**
 * Pure helpers backing the customer intake page.
 *
 * Extracted from the React component so the failure-mode parsing
 * (409 duplicates, 400 validation, network errors) and the small bits
 * of presentational normalisation (phone formatting) can be unit-tested
 * without spinning up a DOM.
 */
import { ApiError } from "@/lib/api";

// ---------- Source channels (mirror of server INTAKE_SOURCES) -----------

/**
 * Must mirror INTAKE_SOURCES in src/server/routes/customerIntake.ts and
 * the customers_source_chk constraint in migration 091. If the office
 * adds a new channel, update all three places in the same change.
 */
export const INTAKE_SOURCES = [
    { value: "phone", label: "Phone call" },
    { value: "web_form", label: "Web form / website" },
    { value: "referral_customer", label: "Referral — existing customer" },
    { value: "referral_trade", label: "Referral — trade partner" },
    { value: "repeat", label: "Repeat customer / walk-in" },
    { value: "marketing", label: "Marketing / advertising" },
    { value: "water_dept_notice", label: "Water department notice" },
    { value: "test_due_followup", label: "Test-due follow-up" },
] as const;

export type IntakeSource = (typeof INTAKE_SOURCES)[number]["value"];

// ---------- US states ----------------------------------------------------

/**
 * Static list. We could load this from the server but it adds a request
 * for data that hasn't changed since 1959. Kept inline.
 */
export const US_STATES: Array<{ code: string; name: string }> = [
    { code: "AL", name: "Alabama" },
    { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" },
    { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" },
    { code: "DE", name: "Delaware" },
    { code: "DC", name: "District of Columbia" },
    { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" },
    { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" },
    { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" },
    { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" },
    { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" },
    { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" },
    { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" },
    { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" },
    { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" },
    { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" },
    { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" },
    { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" },
    { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" },
    { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" },
    { code: "WY", name: "Wyoming" },
];

// ---------- Phone formatting --------------------------------------------

/**
 * Pretty-print a 10- or 11-digit US phone number on blur. Leaves any
 * other input (international, partial typing, free-form notes like
 * "x4012") untouched so the office can use the field for whatever the
 * caller actually said. The server normalises to digits-only for
 * dedupe regardless of formatting.
 *
 * Examples:
 *   "5551234567"       → "(555) 123-4567"
 *   "1-555-123-4567"   → "1 (555) 123-4567"
 *   "555.123.4567"     → "(555) 123-4567"
 *   "555-123"          → "555-123"        (too short, untouched)
 *   "+44 20 7946 0958" → "+44 20 7946 0958" (non-US, untouched)
 */
export function formatPhoneOnBlur(raw: string): string {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return trimmed;
    if (trimmed.startsWith("+")) return trimmed;

    const digits = trimmed.replace(/\D/g, "");
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith("1")) {
        return `1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return trimmed;
}

// ---------- Server error parsing ----------------------------------------

export type IntakeConflict = {
    field: "company_name" | "phone" | "address_with_match" | string;
    conflict_customer_id: string;
    conflict_company_name: string;
    detail: string | null;
};

/**
 * Field-level errors map flattened from a Zod error response. Keys are
 * dot-paths (e.g. "contact.email", "site.postalCode") so the form can
 * highlight the offending input. `formErrors` are top-level messages
 * unrelated to a single field (cross-field refines that don't include
 * a `path`).
 */
export type IntakeValidationDetails = {
    fieldErrors: Record<string, string[]>;
    formErrors: string[];
};

export type ParsedIntakeError =
    | { kind: "duplicate"; conflicts: IntakeConflict[] }
    | { kind: "validation"; details: IntakeValidationDetails }
    | { kind: "auth"; message: string }
    | { kind: "network"; message: string }
    | { kind: "unknown"; message: string };

/**
 * Fold the variety of failure shapes the intake endpoint can return
 * into a single discriminated union the UI can switch on.
 *
 * Server contract (see crm.ts → POST /customers/intake):
 *   - 409 { error: 'DUPLICATE_CUSTOMER', conflicts: [...] }
 *   - 400 { error: 'VALIDATION', details: { fieldErrors, formErrors } }
 *   - 401 { error: 'UNAUTHORIZED' }
 *   - other → unknown
 */
export function parseIntakeError(err: unknown): ParsedIntakeError {
    if (err instanceof ApiError) {
        const body = isObject(err.body) ? err.body : {};

        if (err.status === 409 && body["error"] === "DUPLICATE_CUSTOMER") {
            const conflicts = Array.isArray(body["conflicts"]) ? (body["conflicts"] as unknown[]) : [];
            return { kind: "duplicate", conflicts: conflicts.filter(isConflict) };
        }

        if (err.status === 400 && body["error"] === "VALIDATION") {
            const details = isObject(body["details"]) ? body["details"] : {};
            return {
                kind: "validation",
                details: {
                    fieldErrors: normaliseFieldErrors(details["fieldErrors"]),
                    formErrors: normaliseStringArray(details["formErrors"]),
                },
            };
        }

        if (err.status === 401 || err.status === 403) {
            return { kind: "auth", message: "You don't have permission to create a customer." };
        }

        return { kind: "unknown", message: `Server error (${err.status} ${err.statusText}).` };
    }

    if (err instanceof TypeError) {
        // Fetch network failures land here (DNS, offline, CORS).
        return { kind: "network", message: "Network error — check your connection and try again." };
    }

    return { kind: "unknown", message: "Something went wrong saving the customer." };
}

// ---------- Internal type guards / coercion -----------------------------

function isObject(v: unknown): v is Record<string, unknown> {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isConflict(v: unknown): v is IntakeConflict {
    if (!isObject(v)) return false;
    return typeof v["field"] === "string" && typeof v["conflict_customer_id"] === "string";
}

function normaliseFieldErrors(v: unknown): Record<string, string[]> {
    if (!isObject(v)) return {};
    const out: Record<string, string[]> = {};
    for (const [k, val] of Object.entries(v)) {
        const arr = normaliseStringArray(val);
        if (arr.length > 0) out[k] = arr;
    }
    return out;
}

function normaliseStringArray(v: unknown): string[] {
    if (!Array.isArray(v)) return [];
    return v.filter((x): x is string => typeof x === "string");
}

// ---------- Form payload shaping ----------------------------------------

/**
 * Strip empty strings → undefined before sending to the server. Zod's
 * .optional() rejects empty-string inputs for fields like email, and
 * the office filling in a single field then deleting it shouldn't
 * trigger a 400. Whitespace-only values are also nulled out.
 */
export function blankToUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
        if (typeof v === "string") {
            const trimmed = v.trim();
            if (trimmed.length > 0) out[k] = trimmed;
        } else if (v !== undefined && v !== null) {
            out[k] = v;
        }
    }
    return out as Partial<T>;
}
